<template>
  <CommonLayout>
    <div class="operation-data-content">
      <el-card style="height: 240px;">
        <template #header>
          <div class="card-header">
            <el-icon><Document /></el-icon>
            <span>剩余用量</span>
            <el-icon @click="refreshData" :class="{ 'refresh-icon': true, 'loading': refreshing }">
              <!-- 根据刷新状态显示不同图标 -->
              <template v-if="refreshing">
                <Loading />
              </template>
              <template v-else>
                <Refresh />
              </template>
            </el-icon>
          </div>
        </template>
        <div class="card-content">
          <div v-if="loading || refreshing">加载中...</div>
          <div v-else-if="errorMessage">{{ errorMessage }}</div>
          <div v-else class="operation-data-grid">
            <!-- 上传剩余次数 -->
            <div class="operation-data-item">
              <!-- 修改此处，添加数字滚动组件 -->
              <NumberScroller :targetValue="operationData.uploadNum" :initialValue="0" />
              <span class="operation-data-label">上传次数</span>
            </div>
            <!-- 下载剩余次数 -->
            <div class="operation-data-item">
              <NumberScroller :targetValue="operationData.downloadNum" :initialValue="0" />
              <span class="operation-data-label">下载次数</span>
            </div>
            <!-- 查询剩余次数 -->
            <div class="operation-data-item">
              <NumberScroller :targetValue="operationData.queryNum" :initialValue="0" />
              <span class="operation-data-label">查询次数</span>
            </div>
            <!-- 删除剩余次数 -->
            <div class="operation-data-item">
              <NumberScroller :targetValue="operationData.deleteNum" :initialValue="0" />
              <span class="operation-data-label">删除次数</span>
            </div>
          </div>
        </div>
      </el-card>
      <!-- 新增表格 -->
      <el-card style="margin-top: 20px;">
        <template #header>
          <div class="card-header">
            <el-icon><Document /></el-icon>
            <span>操作记录</span>
          </div>
        </template>
        <div class="card-content">
          <el-table :data="operationRecords" style="width: 100%">
            <template #empty>
              <div style="text-align: center;">暂无数据</div>
            </template>
            <el-table-column prop="username" label="手机号" />
            <el-table-column prop="fosFileId" label="Fos文件ID" width="400" />
            <el-table-column prop="operationName" label="操作" />
            <el-table-column prop="createTime" label="创建时间" />
          </el-table>
          <el-pagination
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
            :current-page="currentPage"
            :page-sizes="[10, 20, 30]"
            :page-size="pageSize"
            layout="total, sizes, prev, pager, next, jumper"
            :total="totalRecords"
          >
          </el-pagination>
        </div>
      </el-card>
      <!-- 修改提示语内容 -->
      <div class="data-update-tip">提示：当前数据非实时更新，请注意使用量。</div>
    </div>
  </CommonLayout>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { ElMessage } from 'element-plus'
import { generateTimestamp, generateToken } from '@/utils/crypto'
import { API_PATHS } from '@/config/api'
import CommonLayout from '@/components/CommonLayout.vue'
import service from '@/utils/http'
import { Refresh, Loading } from '@element-plus/icons-vue' // 引入刷新和加载图标
import NumberScroller from '@/components/NumberScroller.vue'; // 引入数字滚动组件

const userInfoStr = localStorage.getItem('userInfo')
const userInfo = userInfoStr ? JSON.parse(userInfoStr) : null
const userId = ref(userInfo ? userInfo.userId : null)

const operationData = ref({})
const loading = ref(true)
const errorMessage = ref('')
const refreshing = ref(false) // 新增刷新状态变量

const getOperationData = async () => {
  try {
    const timestamp = generateTimestamp()
    const params = {
      userId: userId.value,
      timestamp,
      token: generateToken({ userId: userId.value, timestamp })
    }

    const response = await service.get(
      `${API_PATHS.operationList}`,
      { params }
    )
    operationData.value = response.data[0] || {}
  } catch (error) {
    errorMessage.value = error.message || '获取操作数据失败'
  } finally {
    loading.value = false
    refreshing.value = false // 刷新完成，重置刷新状态
  }
}

const refreshData = () => {
  refreshing.value = true // 开始刷新，设置刷新状态
  getOperationData()
  getOperationRecords() // 刷新时重新获取操作记录
}

onMounted(() => {
  if (!userInfo) {
    ElMessage.error('未获取到用户信息，请重新登录')
    router.push('/auth') 
  } else {
    getOperationData()
    getOperationRecords()
  }
})

// 新增表格相关数据和方法
const operationRecords = ref([])
const currentPage = ref(1)
const pageSize = ref(10)
const totalRecords = ref(0)

const getOperationRecords = async () => {
  try {
    const timestamp = generateTimestamp()
    const params = {
      userId: userId.value,
      currentPage: currentPage.value,
      size: pageSize.value,
      timestamp,
      token: generateToken({ userId: userId.value, currentPage: currentPage.value, size: pageSize.value, timestamp })
    }

    const response = await service.get(
      `${API_PATHS.operationRecordList}`,
      { params }
    )
    operationRecords.value = response.data.list || []
    totalRecords.value = response.data.size
  } catch (error) {
    ElMessage.error(error.message || '获取操作记录失败')
  }
}

const handleSizeChange = (newSize) => {
  pageSize.value = newSize
  getOperationRecords()
}

const handleCurrentChange = (newPage) => {
  currentPage.value = newPage
  getOperationRecords()
}
</script>

<style scoped>
.operation-data-content {
  padding: 20px;
  /* 移除背景色 */
  background-color: transparent; 
  /* 移除圆角 */
  border-radius: 0; 
  /* 移除阴影 */
  box-shadow: none; 
  /* 设置整个框宽度占页面的66% */
  width: 100%;
  /* 移除 margin: 0 auto，让框居左 */
}

.data-update-tip {
  /* 修改提示语颜色为红色 */
  color: red; 
  font-size: 12px; /* 提示语字体大小 */
  margin-top: 10px; /* 提示语底部间距 */
}

.card-header {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 500;
}

.card-header .el-icon {
  font-size: 20px;
  color: #409EFF;
}

/* 刷新图标样式 */
.refresh-icon {
  margin-left: auto; /* 靠右对齐 */
  cursor: pointer; /* 鼠标指针样式 */
  color: #999; /* 图标颜色 */
  transition: color 0.3s; /* 颜色过渡效果 */
}

.refresh-icon.loading {
  animation: spin 1s linear infinite; /* 加载动画 */
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.refresh-icon:hover {
  color: #409EFF; /* 鼠标悬停时图标颜色 */
}

.card-content {
  padding: 20px;
  background-color: #fff; /* 卡片内容背景颜色 */
  border-radius: 4px; /* 卡片内容圆角 */
}

.el-table {
  width: 100%;
}

.el-pagination {
  margin-top: 20px;
  text-align: right;
}

/* 新增样式 */
.operation-data-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
}

.operation-data-item {
  text-align: center;
}

.operation-data-number {
  font-size: 48px;
  font-weight: bold;
}

.operation-data-label {
  font-size: 14px;
  color: #999;
  display: block;
}
</style>